import avatar from "@/assets/jenifer-avatar.webp";
import { CalendarCheck, Instagram, MonitorSmartphone, TrendingUp, Workflow } from "lucide-react";
import { SunburstDiagram } from "@/components/brand/SunburstDiagram";
import { TextLink } from "@/components/ui/Button";
import { CtaReassurance, FreeAuditButton } from "@/components/ui/Cta";
import { Parallax } from "@/components/ui/Parallax";
import { useContent } from "@/data/content";
import { useAppTranslation } from "@/i18n/useAppTranslation";

export const CHANNEL_ICONS = [Instagram, MonitorSmartphone, CalendarCheck, Workflow, TrendingUp] as const;

export function HeroSection() {
  const { hero } = useContent().home;
  const { t } = useAppTranslation("common");
  const channels = hero.channels.map((label, i) => ({ label, icon: CHANNEL_ICONS[i] }));

  return (
    <section
      aria-labelledby="hero-heading"
      className="light-pool relative overflow-hidden [--pool-x:78%] [--pool-y:42%]"
    >
      <div className="relative mx-auto grid max-w-7xl items-center gap-6 px-4 pb-16 pt-28 sm:px-6 sm:pt-32 lg:min-h-[min(100svh,64rem)] lg:grid-cols-[1.05fr_1fr] lg:gap-10 lg:px-8 lg:pb-20 lg:pt-28">
        <div className="max-w-xl lg:max-w-none">
          <p className="hero-rise flex items-center gap-3">
            <img
              src={avatar}
              alt=""
              width={56}
              height={56}
              fetchPriority="high"
              className="size-12 shrink-0 rounded-full object-cover shadow-lit ring-2 ring-white"
            />
            <span className="leading-tight">
              <span className="block font-semibold text-brand-primary">{hero.byline.name}</span>
              <span className="block text-sm text-ink-muted">{hero.byline.role}</span>
            </span>
          </p>
          <h1
            id="hero-heading"
            className="hero-rise mt-7 font-display text-[clamp(2.6rem,6.2vw,4.75rem)] font-semibold leading-[1.02] tracking-[-0.03em] text-brand-primary"
          >
            {hero.title}
          </h1>
          <p
            className="hero-rise mt-6 max-w-[34rem] text-lg leading-relaxed text-ink-soft sm:text-xl sm:leading-relaxed"
            style={{ ["--delay" as string]: "90ms" }}
          >
            {hero.subtitle}
          </p>
          <div
            className="hero-rise mt-9 flex flex-col items-start gap-5 sm:flex-row sm:items-start sm:gap-7"
            style={{ ["--delay" as string]: "180ms" }}
          >
            <div>
              <FreeAuditButton location="hero" />
              <CtaReassurance className="mt-3 max-w-[22rem]" />
            </div>
            <div className="sm:flex sm:min-h-13 sm:items-center">
              <TextLink to="/precios">{t("seePlans")}</TextLink>
            </div>
          </div>
        </div>

        <Parallax mode="leave" distance={90} className="mx-auto w-full max-w-[34rem] lg:max-w-none">
          <div className="hero-rise" style={{ ["--delay" as string]: "140ms" }}>
            <SunburstDiagram
              channels={channels}
              centerLabel={hero.center}
              centerDone={hero.centerDone}
              booking={hero.booking}
              ariaLabel={hero.diagramLabel}
            />
          </div>
        </Parallax>
      </div>
    </section>
  );
}
